import { useMutation } from "@tanstack/react-query";
import type { Employee } from "models";
import { DealerMsg, EmployeeMsg } from "sections/admin/Constants";
import { EmployeeService } from "sections/employee";
import { useUI } from "services/UI";

type EmployeeAddPayload = Parameters<typeof EmployeeService.add>[0];

export function useEmployeeMutations() {
  const { showSuccess, showError } = useUI();

  const addMutation = useMutation({
    mutationFn: async (payload: EmployeeAddPayload) => {
      const { createdAt, dealerName, ...rest } = payload as Record<string, unknown> & {
        createdAt?: unknown;
        dealerName?: unknown;
      };
      return EmployeeService.add(rest as EmployeeAddPayload);
    },
    onSuccess: () => {
      showSuccess({
        title: DealerMsg.SUCCESS_TITLE,
        msg: "Employee added successfully.",
      });
    },
    onError: (error) => {
      showError({ title: "Error", msg: error.message || "An error occurred" });
    },
  });

  const editMutation = useMutation({
    mutationFn: async (payload: Employee) => {
      const body = { ...payload, dealerName: undefined };
      return EmployeeService.set(
        body as Parameters<typeof EmployeeService.set>[0],
        "PATCH",
        false,
      );
    },
    onSuccess: () => {
      showSuccess({
        title: DealerMsg.SUCCESS_TITLE,
        msg: "Employee updated successfully.",
      });
    },
    onError: (error) => {
      showError({ title: "Error", msg: error.message || "An error occurred" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => EmployeeService.del(id),
    onSuccess: () => {
      showSuccess({
        title: DealerMsg.SUCCESS_TITLE,
        msg: "Employee deleted successfully.",
      });
    },
    onError: (error) => {
      showError({ title: "Error", msg: error.message || "An error occurred" });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => EmployeeService.resetEmployeePassword(email),
    onSuccess: () => {
      showSuccess({
        title: "Success!",
        msg: "Password reset email sent.",
      });
    },
    onError: (error) => {
      showError({ title: "Error", msg: error.message || "An error occurred" });
    },
  });

  const isPending =
    addMutation.isPending ||
    editMutation.isPending ||
    deleteMutation.isPending ||
    resetPasswordMutation.isPending;

  return {
    addEmployee: addMutation.mutate,
    addPending: addMutation.isPending,
    editEmployee: editMutation.mutate,
    editPending: editMutation.isPending,
    deleteEmployee: deleteMutation.mutate,
    deletePending: deleteMutation.isPending,
    resetEmployeePassword: resetPasswordMutation.mutate,
    resetPasswordPending: resetPasswordMutation.isPending,
    isPending,
  };
}
